import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.bychef.am/';

/**
 * Get authentication token from localStorage
 * @returns {string|null} The auth token or null if not found
 */
const getAuthToken = () => {
    return localStorage.getItem('authToken');
};

/**
 * Request interceptor to add auth token to requests
 * @param {Object} config - Axios request config
 * @returns {Object} Modified config with auth header
 */
const addAuthToken = (config) => {
    const token = getAuthToken();
    if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
};

/**
 * Response interceptor to handle authentication errors
 * @param {Error} error - Axios error object
 * @returns {Promise} Rejected promise with error
 */
const handleAuthError = (error) => {
    // Only handle authentication errors (401 Unauthorized, 403 Forbidden)
    // Don't log out on 400 Bad Request (validation errors) or other 4XX errors
    if (error.response && 
        (error.response.status === 401 || error.response.status === 403)) {
        
        const url = error.config?.url || '';
        
        // For /me endpoint or any authenticated endpoint, clear token and redirect
        if (url.includes('/me') || url.includes('/kms/')) {
            localStorage.removeItem('authToken');
            
            // Only redirect if not already on login page
            if (!window.location.pathname.includes('/login')) {
                window.location.href = '/login';
            }
        }
    }
    return Promise.reject(error);
};

// Create axios instances
export const apiJson = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json'
    },
});

export const apiMultipart = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'multipart/form-data',
    },
});

// Add request interceptors to inject auth tokens
apiJson.interceptors.request.use(addAuthToken, (error) => Promise.reject(error));
apiMultipart.interceptors.request.use(addAuthToken, (error) => Promise.reject(error));

// Add response interceptors to handle auth errors
apiJson.interceptors.response.use(
    (response) => response,
    handleAuthError
);

apiMultipart.interceptors.response.use(
    (response) => response,
    handleAuthError
);

/**
 * Check if the page is currently visible to the user
 * @returns {boolean} True if page is visible, false otherwise
 */
export const isPageVisible = () => {
    if (typeof document === 'undefined') return true;
    return !document.hidden;
};

/**
 * Check if an error is a network error (not a server error)
 * @param {Error} error - The error object
 * @returns {boolean} True if it's a network error, false otherwise
 */
export const isNetworkError = (error) => {
    // Network errors don't have a response object
    if (!error.response) {
        // Check error code first (axios sets this for network errors)
        const errorCode = error.code || '';
        const networkErrorCodes = [
            'ERR_NETWORK',
            'ERR_INTERNET_DISCONNECTED',
            'ERR_NETWORK_CHANGED',
            'ERR_CONNECTION_REFUSED',
            'ERR_CONNECTION_RESET',
            'ERR_CONNECTION_TIMED_OUT',
            'ERR_NAME_NOT_RESOLVED',
            'ECONNABORTED',
            'ETIMEDOUT',
            'ENOTFOUND'
        ];
        
        if (networkErrorCodes.some(code => errorCode.includes(code))) {
            return true;
        }
        
        // Check for common network error messages
        const errorMessage = (error.message || error.toString() || '').toLowerCase();
        const networkErrorPatterns = [
            'network error',
            'err_internet_disconnected',
            'err_network_changed',
            'err_connection_refused',
            'err_connection_reset',
            'err_connection_timed_out',
            'err_name_not_resolved',
            'failed to fetch',
            'timeout',
            'econnaborted',
            'network request failed'
        ];
        
        return networkErrorPatterns.some(pattern => 
            errorMessage.includes(pattern)
        );
    }
    return false;
};

/**
 * Check if an error should be shown to the user
 * @param {Error} error - The error object
 * @returns {boolean} True if error should be shown, false otherwise
 */
export const shouldShowError = (error) => {
    // Always show server errors (4xx, 5xx)
    if (error.response) {
        return true;
    }
    
    // Only show network errors if page is visible
    if (isNetworkError(error)) {
        return isPageVisible();
    }
    
    // For other errors, show if page is visible
    return isPageVisible();
};